/**
 * W5 — "View <Technician>'s Bays" (mockup panel 3): the technician workspace.
 *
 * Read model: technician_workspace_day (server). The dialog never synthesises a state:
 *   - ONE hands-on labor row (measured; the only labor-credited time; never overlapping);
 *   - one lane per vehicle, labelled with its current bay, carrying hands-on, unattended (machine, no labor),
 *     switched-away / paused (measured waits, no labor) and planned windows (outlined, never solid);
 *   - predicted times are labelled "Predicted" / "est." and drawn differently from measured ones;
 *   - unknown stays unknown ("—", "until ended").
 *
 * Actions (only on the signed-in technician's OWN workspace; every one is a server authority that refuses what it
 * must): switch hands-on work to a waiting vehicle (switch_my_work, optionally leaving the current vehicle on a
 * machine), start a machine process on the current vehicle, end a machine process. Nothing here starts labor
 * that the technician did not ask for, and nothing stops a running timer because the screen changed.
 */
import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Wrench, Cog, PauseCircle, CalendarClock, ArrowRightLeft, Table as TableIcon, Clock } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useFormFactor } from '@/hooks/use-mobile';
import { clock } from '@/utils/clock';
import {
  UNATTENDED_KINDS, unattendedKindLabel, switchMyWork, startUnattendedOperation, endUnattendedOperation,
  workSwitchOutcomeCopy, newRequestId, type UnattendedKind,
} from '@/lib/timer/workSwitch';
import { requestServerFinish } from '@/utils/serverFinishIntent';
import {
  buildTechnicianWorkspace, formatMinutes, formatShopTime, type TimelineBlock, type VehicleLane,
} from './buildTechnicianWorkspace';
import { useTechnicianWorkspaceDay } from './useTechnicianWorkspaceDay';
import { useUnattendedOperationFlags } from '@/hooks/useUnattendedOperationFlags';
import { UnattendedFlagBadges } from '../multibay/UnattendedFlagBadges';

export interface TechnicianWorkspaceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  technician: { id: string; name: string; shop_id?: string | null };
  dayLabel: string;
  /** The surface allows actions (not a read-only board). Controls appear only when the SERVER also says the viewer
   *  is this technician (technician_workspace_day.viewer_is_technician); every act is still its authority's. */
  canAct: boolean;
  /** A manager may end a machine process (conservative 5-f: starter or manager). */
  canEndAnyOperation?: boolean;
}

function ticketLabel(n: number | null | undefined): string {
  return n != null ? `TKT-${String(n).padStart(4, '0')}` : 'Ticket';
}

const BLOCK_STYLE: Record<TimelineBlock['kind'], string> = {
  hands_on: 'bg-[hsl(var(--status-active))] text-white border border-[hsl(var(--status-active))]',
  unattended: 'bg-[hsl(var(--success))]/25 border-2 border-[hsl(var(--success))] text-foreground bg-[repeating-linear-gradient(45deg,transparent,transparent_4px,hsl(var(--success)/0.25)_4px,hsl(var(--success)/0.25)_8px)]',
  switched_away: 'bg-[repeating-linear-gradient(135deg,transparent,transparent_4px,hsl(var(--status-paused)/0.35)_4px,hsl(var(--status-paused)/0.35)_8px)] border border-[hsl(var(--status-paused))] text-foreground',
  paused: 'bg-[repeating-linear-gradient(135deg,transparent,transparent_4px,hsl(var(--status-paused)/0.35)_4px,hsl(var(--status-paused)/0.35)_8px)] border border-[hsl(var(--status-paused))] text-foreground',
  planned: 'bg-transparent border-2 border-dashed border-muted-foreground/60 text-muted-foreground',
};

function TimelineTrack({ blocks, nowLeftPct, laneLabel }: { blocks: TimelineBlock[]; nowLeftPct: number | null; laneLabel: string }) {
  return (
    <div className="relative h-9 rounded bg-muted/40" role="row" aria-label={laneLabel}>
      {blocks.map((b) => (
        <div
          key={b.id}
          role="gridcell"
          tabIndex={0}
          aria-label={b.label}
          title={b.label}
          data-kind={b.kind}
          data-basis={b.timeBasis}
          data-labor-credit={b.laborCredit ? 'true' : 'false'}
          className={cn(
            'absolute top-1 bottom-1 rounded-sm px-1 text-[10px] leading-7 overflow-hidden whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-ring',
            BLOCK_STYLE[b.kind],
            b.kind === 'planned' && 'top-2 bottom-2 leading-5',
            b.conflict && 'ring-2 ring-[hsl(var(--status-blocked))]',
            b.open && b.kind !== 'planned' && 'motion-safe:animate-pulse',
          )}
          style={{ left: `${b.leftPct}%`, width: `${b.widthPct}%` }}
        >
          {b.conflict ? '! ' : ''}
        </div>
      ))}
      {blocks.map((b) => (b.kind === 'unattended' && b.expectedEndMs != null ? (
        <div key={`${b.id}:exp`} aria-hidden className="absolute top-0 bottom-0 border-l-2 border-dotted border-[hsl(var(--success))]" style={{ left: `${Math.min(100, b.leftPct + ((b.expectedEndMs - b.startMs) / Math.max(1, b.endMs - b.startMs)) * b.widthPct)}%` }} />
      ) : null))}
      {nowLeftPct != null && <div aria-hidden className="absolute top-0 bottom-0 w-0.5 bg-[hsl(var(--status-blocked))]" style={{ left: `${nowLeftPct}%` }} />}
    </div>
  );
}

function UnattendedForm({ onSubmit, busy, submitLabel }: {
  onSubmit: (v: { kind: UnattendedKind; expectedMinutes: number | null; note: string | null }) => void;
  busy: boolean;
  submitLabel: string;
}) {
  const [kind, setKind] = useState<UnattendedKind>('AC_CYCLE');
  const [minutes, setMinutes] = useState('');
  const [note, setNote] = useState('');
  const m = minutes.trim() === '' ? null : Number(minutes);
  const invalid = (m != null && (!Number.isInteger(m) || m < 1 || m > 1440)) || (kind === 'OTHER' && note.trim() === '');
  return (
    <div className="grid gap-2 rounded-md border p-3 bg-muted/20">
      <div className="grid gap-1">
        <Label htmlFor="w5-kind">Machine process (stated, never guessed)</Label>
        <select id="w5-kind" className="h-9 rounded-md border bg-background px-2 text-sm" value={kind}
          onChange={(e) => setKind(e.target.value as UnattendedKind)}>
          {UNATTENDED_KINDS.map((k) => <option key={k} value={k}>{unattendedKindLabel(k)}</option>)}
        </select>
      </div>
      <div className="grid gap-1">
        <Label htmlFor="w5-min">Expected minutes (optional — leave blank if unknown)</Label>
        <Input id="w5-min" inputMode="numeric" value={minutes} onChange={(e) => setMinutes(e.target.value)} placeholder="e.g. 30" />
      </div>
      {kind === 'OTHER' && (
        <div className="grid gap-1">
          <Label htmlFor="w5-note">What is the machine doing?</Label>
          <Input id="w5-note" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      )}
      <p className="text-xs text-muted-foreground">Unattended time holds the vehicle&apos;s bay. It is never recorded as labor.</p>
      <Button size="sm" disabled={busy || invalid} onClick={() => onSubmit({ kind, expectedMinutes: m, note: note.trim() || null })}>
        {submitLabel}
      </Button>
    </div>
  );
}

export function TechnicianWorkspaceDialog({ open, onOpenChange, technician, dayLabel, canAct: surfaceAllowsActions, canEndAnyOperation = false }: TechnicianWorkspaceDialogProps) {
  const formFactor = useFormFactor();
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useTechnicianWorkspaceDay(technician.id, dayLabel, open, technician.shop_id ?? null);
  // B-T1: the server's flags on each open machine process (display only; never ends anything).
  const flagsByOperationId = useUnattendedOperationFlags(technician.shop_id ?? null, open);
  const nowMs = clock.nowMs();
  const tl = useMemo(() => (data ? buildTechnicianWorkspace(data, nowMs) : null), [data, nowMs]);
  const tz = data?.time_zone || 'UTC';
  const [asTable, setAsTable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [panel, setPanel] = useState<null | { mode: 'switch'; to: VehicleLane; leaveMachine: boolean } | { mode: 'machine' }>(null);
  const requestIdRef = useRef<string | null>(null);
  const [announce, setAnnounce] = useState('');

  const canAct = surfaceAllowsActions && data?.viewer_is_technician === true;
  const active = data?.active ?? null;
  const activeLane = tl?.lanes.find((l) => l.ticketId === active?.ticket_id) ?? null;
  const ownRequestId = () => (requestIdRef.current ??= newRequestId());
  const settle = (ok: boolean) => { if (ok) requestIdRef.current = null; };

  const report = (outcome: string) => {
    const c = workSwitchOutcomeCopy(outcome);
    (c.tone === 'success' ? toast.success : c.tone === 'warning' ? toast.warning : toast.error)(c.title, { description: c.description });
    setAnnounce(c.title);
  };
  const afterChange = () => {
    window.dispatchEvent(new Event('ticket-updated'));
    refetch();
  };

  const doSwitch = async (to: VehicleLane, machine: { kind: UnattendedKind; expectedMinutes: number | null; note: string | null } | null) => {
    if (!active || active.source !== 'ticket_timer') return;
    setBusy(true);
    try {
      const r = await switchMyWork({
        requestId: ownRequestId(),
        fromTicketId: active.ticket_id,
        toTicketId: to.ticketId,
        expectedFromSessionId: active.session_id,
        unattended: machine ? { kind: machine.kind, expectedMinutes: machine.expectedMinutes, kindNote: machine.note } : null,
      });
      report(r.outcome);
      settle(r.outcome === 'SWITCHED' || r.outcome === 'STATE_CHANGED' || r.outcome === 'IDEMPOTENCY_KEY_REUSED');
      if (r.outcome === 'SWITCHED') {
        setPanel(null);
        if ('to' in r && r.to.outcome === 'STARTED' && r.to.ticketBecameRunning) {
          void requestServerFinish(r.to.ticketId, 'timer_started', { reason: 'w5 switch_my_work started the target' });
        }
      }
      afterChange();
    } finally { setBusy(false); }
  };

  const doStartMachine = async (machine: { kind: UnattendedKind; expectedMinutes: number | null; note: string | null }) => {
    if (!active) return;
    setBusy(true);
    try {
      const r = await startUnattendedOperation(ownRequestId(), active.ticket_id, { kind: machine.kind, expectedMinutes: machine.expectedMinutes, kindNote: machine.note });
      report(r.outcome);
      settle(r.outcome === 'OPENED' || r.outcome === 'ALREADY_OPEN' || r.outcome === 'IDEMPOTENCY_KEY_REUSED');
      if (r.outcome === 'OPENED') setPanel(null);
      afterChange();
    } finally { setBusy(false); }
  };

  const doEnd = async (operationId: string) => {
    setBusy(true);
    try {
      const r = await endUnattendedOperation(newRequestId(), operationId, 'COMPLETED');
      report(r.outcome);
      afterChange();
    } finally { setBusy(false); }
  };

  const showOnBoard = (ticketId: string) => {
    onOpenChange(false);
    navigate(`/?tab=dayboard&highlight=${encodeURIComponent(ticketId)}&openDetails=true`);
  };

  const pred = data?.predicted_next_available;
  const predText = !pred ? '—'
    : pred.basis === 'free_now' ? 'Free now'
    : pred.at ? `est. ${formatShopTime(new Date(pred.at).getTime(), tz)}` : '—';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="technician-workspace-dialog"
        className={cn(
          formFactor === 'desktop'
            ? 'max-w-[min(1200px,calc(100vw-2rem))]'
            : 'inset-0 left-0 top-0 translate-x-0 translate-y-0 w-screen h-[100dvh] max-h-[100dvh] max-w-none rounded-none',
        )}
      >
        <DialogHeader>
          <DialogTitle>{technician.name} — Technician workspace</DialogTitle>
          <DialogDescription>
            {dayLabel}
            {data?.as_of ? ` · as of ${formatShopTime(new Date(data.as_of).getTime(), tz)}` : ''}
            {' · '}Measured time is solid; planned and predicted times are outlined and labelled.
          </DialogDescription>
        </DialogHeader>
        <div aria-live="polite" className="sr-only">{announce}</div>

        {error && <p role="alert" className="text-sm text-destructive">Could not load the workspace. Nothing on the board changed.</p>}
        {!data && loading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {data && !data.available && <p className="text-sm text-muted-foreground">This workspace is not available.</p>}

        {tl && data && (
          <div className="grid gap-4 min-w-0">
            {/* Summary tiles — each with its basis; never summed together */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="workspace-tiles">
              <div className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground">Hands-on timer time today</div>
                <div className="text-lg font-semibold" data-testid="tile-hands-on">{formatMinutes(data.totals?.hands_on_minutes)}</div>
                <div className="text-[11px] text-muted-foreground">Measured · timer sessions</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground">Unattended machine time</div>
                <div className="text-lg font-semibold" data-testid="tile-unattended">{formatMinutes(data.totals?.unattended_minutes)}</div>
                <div className="text-[11px] text-muted-foreground">Measured · no labor credit</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground">Vehicles in bays</div>
                <div className="text-lg font-semibold" data-testid="tile-bays">{tl.vehiclesInBays} vehicle{tl.vehiclesInBays === 1 ? '' : 's'} · {tl.distinctBays} bay{tl.distinctBays === 1 ? '' : 's'}</div>
                <div className="text-[11px] text-muted-foreground">Physical location now</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground">Next available</div>
                <div className="text-lg font-semibold" data-testid="tile-next-available">{predText}</div>
                <div className="text-[11px] text-muted-foreground">{pred?.basis === 'predicted' ? 'Predicted · current hands-on finish' : pred?.basis === 'free_now' ? 'No hands-on work running' : 'Unknown'}</div>
              </div>
            </div>

            {/* Hands-On Now */}
            <section aria-labelledby="w5-ws-handson" className="rounded-md border p-3">
              <h4 id="w5-ws-handson" className="flex items-center gap-2 text-sm font-semibold"><Wrench className="h-4 w-4" aria-hidden />Hands-On Now</h4>
              {active ? (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm" data-testid="workspace-hands-on-now">
                  <Badge>{active.source === 'inspection_timer' ? 'Inspection' : 'Ticket work'}</Badge>
                  <button type="button" className="font-medium underline" onClick={() => showOnBoard(active.ticket_id)}>{ticketLabel(active.ticket_number)}</button>
                  {activeLane?.vehicleLabel && <span>{activeLane.vehicleLabel}</span>}
                  {activeLane?.bayName && <Badge variant="secondary">{activeLane.bayName}</Badge>}
                  <span className="text-muted-foreground">started {formatShopTime(new Date(active.started_at).getTime(), tz)} · {formatMinutes((nowMs - new Date(active.started_at).getTime()) / 60000)} measured</span>
                  {canAct && active.source === 'ticket_timer' && (
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => { requestIdRef.current = null; setPanel(panel?.mode === 'machine' ? null : { mode: 'machine' }); }}>
                      <Cog className="h-4 w-4 mr-1" aria-hidden />Start machine process here
                    </Button>
                  )}
                </div>
              ) : (
                <p className="mt-2 text-sm text-muted-foreground" data-testid="workspace-hands-on-now">No hands-on work is running.</p>
              )}
              {panel?.mode === 'machine' && <div className="mt-2"><UnattendedForm busy={busy} submitLabel="Start machine process" onSubmit={(v) => void doStartMachine(v)} /></div>}
            </section>

            {/* Timeline: one labor row + one lane per vehicle, one shared horizontal scroller */}
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold flex items-center gap-2"><Clock className="h-4 w-4" aria-hidden />Timeline</h4>
              <Button size="sm" variant="ghost" aria-pressed={asTable} onClick={() => setAsTable((v) => !v)}>
                <TableIcon className="h-4 w-4 mr-1" aria-hidden />{asTable ? 'Show as timeline' : 'Show as table'}
              </Button>
            </div>
            {tl.laborOverlapDetected && (
              <p role="alert" className="text-sm text-destructive">Two measured hands-on intervals overlap — this should be impossible; tell a manager.</p>
            )}
            {!asTable ? (
              <div className="overflow-x-auto rounded-md border" data-testid="workspace-timeline">
                <div role="grid" aria-label={`Timeline for ${technician.name}, ${dayLabel}`} className="min-w-[720px]">
                  <div className="grid grid-cols-[180px_1fr] items-end border-b bg-muted/30">
                    <div className="sticky left-0 bg-muted/30 px-2 py-1 text-xs text-muted-foreground">Shop time</div>
                    <div className="relative h-6">
                      {tl.ticks.map((t) => (
                        <span key={t.ms} className="absolute -translate-x-1/2 text-[10px] text-muted-foreground" style={{ left: `${t.leftPct}%` }}>{t.label}</span>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-[180px_1fr] items-center border-b">
                    <div className="sticky left-0 z-10 bg-card px-2 py-1 text-xs font-medium">Hands-on labor<div className="text-[10px] font-normal text-muted-foreground">measured · one at a time</div></div>
                    <div className="px-1 py-1"><TimelineTrack blocks={tl.laborRow} nowLeftPct={tl.nowLeftPct} laneLabel="Hands-on labor, measured" /></div>
                  </div>
                  {tl.lanes.map((lane) => (
                    <div key={lane.ticketId} className="grid grid-cols-[180px_1fr] items-center border-b last:border-b-0" data-testid={`workspace-lane-${lane.ticketId}`}>
                      <div className="sticky left-0 z-10 bg-card px-2 py-1 text-xs">
                        <div className="font-medium">
                          {lane.bayName ?? 'No bay'}
                          {lane.bayBasis === 'planned' && <span className="text-muted-foreground"> (planned)</span>}
                        </div>
                        <button type="button" className="underline" onClick={() => showOnBoard(lane.ticketId)}>{ticketLabel(lane.ticketNumber)}</button>
                        <div className="text-[10px] text-muted-foreground truncate">{lane.stateLabel}</div>
                      </div>
                      <div className="px-1 py-1"><TimelineTrack blocks={lane.blocks} nowLeftPct={tl.nowLeftPct} laneLabel={`${lane.bayName ?? 'No bay'}, ${ticketLabel(lane.ticketNumber)}, ${lane.stateLabel}`} /></div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm" data-testid="workspace-table">
                  <caption className="sr-only">Blocks for {technician.name}, {dayLabel}</caption>
                  <thead><tr className="text-left text-xs text-muted-foreground"><th className="p-2">What</th><th className="p-2">Ticket</th><th className="p-2">From</th><th className="p-2">To</th><th className="p-2">Basis</th><th className="p-2">Labor credit</th></tr></thead>
                  <tbody>
                    {[...tl.laborRow, ...tl.lanes.flatMap((l) => l.blocks.filter((b) => b.kind !== 'hands_on'))]
                      .sort((a, b) => a.startMs - b.startMs)
                      .map((b) => (
                        <tr key={`t:${b.id}`} className="border-t">
                          <td className="p-2">{b.kind.replace('_', ' ')}</td>
                          <td className="p-2">{tl.lanes.find((l) => l.ticketId === b.ticketId)?.ticketNumber != null ? ticketLabel(tl.lanes.find((l) => l.ticketId === b.ticketId)?.ticketNumber) : b.ticketId}</td>
                          <td className="p-2">{formatShopTime(b.startMs, tz)}</td>
                          <td className="p-2">{b.open ? 'still running' : formatShopTime(b.endMs, tz)}</td>
                          <td className="p-2">{b.timeBasis}</td>
                          <td className="p-2">{b.laborCredit ? 'yes' : 'no'}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Vehicles: bay occupancy, unattended processes, switching */}
            <section aria-labelledby="w5-ws-vehicles" className="grid gap-2">
              <h4 id="w5-ws-vehicles" className="text-sm font-semibold">Vehicles and bays</h4>
              {tl.lanes.length === 0 && <p className="text-sm text-muted-foreground">No vehicles today.</p>}
              {tl.lanes.map((lane) => {
                const isActive = lane.ticketId === active?.ticket_id;
                const waitingForMe = lane.state === 'SWITCHED_AWAY' || lane.state.startsWith('PAUSED_')
                  || (lane.state === 'UNATTENDED_RUNNING' && !!data.vehicles?.find((v) => v.ticket_id === lane.ticketId)?.open_pause_intent);
                return (
                  <div key={`v:${lane.ticketId}`} className="rounded-md border p-3 text-sm" data-testid={`workspace-vehicle-${lane.ticketId}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      {lane.state === 'HANDS_ON' ? <Wrench className="h-4 w-4" aria-hidden /> : lane.state === 'UNATTENDED_RUNNING' ? <Cog className="h-4 w-4" aria-hidden /> : lane.state === 'NOT_STARTED' ? <CalendarClock className="h-4 w-4" aria-hidden /> : <PauseCircle className="h-4 w-4" aria-hidden />}
                      <button type="button" className="font-medium underline" onClick={() => showOnBoard(lane.ticketId)}>{ticketLabel(lane.ticketNumber)}</button>
                      {lane.vehicleLabel && <span>{lane.vehicleLabel}</span>}
                      <Badge variant="secondary">{lane.bayName ?? 'No bay'}{lane.bayBasis === 'planned' ? ' (planned)' : ''}</Badge>
                      <span className="text-muted-foreground">{lane.stateLabel}</span>
                      {lane.predictedFinishMs != null && <span className="text-muted-foreground">· vehicle finish est. {formatShopTime(lane.predictedFinishMs, tz)} (predicted)</span>}
                    </div>
                    {lane.openUnattended.map((op) => (
                      <div key={op.id} className="mt-2 flex flex-wrap items-center gap-2 rounded bg-muted/30 p-2" data-testid={`workspace-op-${op.id}`}>
                        <Cog className="h-4 w-4" aria-hidden />
                        <span className="font-medium">{unattendedKindLabel(op.kind)}{op.kind_note ? ` — ${op.kind_note}` : ''}</span>
                        <span>since {formatShopTime(new Date(op.started_at).getTime(), tz)}</span>
                        <span className="text-muted-foreground">
                          {op.expected_end_at ? `expected end ${formatShopTime(new Date(op.expected_end_at).getTime(), tz)} (stated)` : 'until ended (no expected end stated)'}
                        </span>
                        {op.past_expected_end && <Badge variant="destructive">Past expected end</Badge>}
                        <UnattendedFlagBadges flags={flagsByOperationId.get(op.id)} />
                        <span className="text-[11px] text-muted-foreground">No labor recorded</span>
                        {((canAct && op.started_by_technician_id === technician.id) || canEndAnyOperation) && (
                          <Button size="sm" variant="outline" disabled={busy} onClick={() => void doEnd(op.id)}>End machine process</Button>
                        )}
                      </div>
                    ))}
                    {canAct && !isActive && waitingForMe && active?.source === 'ticket_timer' && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Button size="sm" disabled={busy} onClick={() => { requestIdRef.current = null; setPanel({ mode: 'switch', to: lane, leaveMachine: false }); }}>
                          <ArrowRightLeft className="h-4 w-4 mr-1" aria-hidden />Switch hands-on work here
                        </Button>
                      </div>
                    )}
                    {canAct && !isActive && waitingForMe && !active && (
                      <p className="mt-2 text-xs text-muted-foreground">Nothing is running — resume this vehicle from its card.</p>
                    )}
                    {panel?.mode === 'switch' && panel.to.ticketId === lane.ticketId && (
                      <div className="mt-2 grid gap-2 rounded-md border p-3" data-testid="workspace-switch-panel">
                        <p>
                          Pause {ticketLabel(active?.ticket_number)} (its measured time is kept) and resume hands-on work on {ticketLabel(lane.ticketNumber)}.
                        </p>
                        <label className="flex items-center gap-2">
                          <input type="checkbox" checked={panel.leaveMachine} onChange={(e) => setPanel({ ...panel, leaveMachine: e.target.checked })} />
                          Leave {ticketLabel(active?.ticket_number)} running on a machine (unattended)
                        </label>
                        {panel.leaveMachine
                          ? <UnattendedForm busy={busy} submitLabel="Switch and start the machine process" onSubmit={(v) => void doSwitch(lane, v)} />
                          : (
                            <div className="flex gap-2">
                              <Button size="sm" disabled={busy} onClick={() => void doSwitch(lane, null)}>Switch</Button>
                              <Button size="sm" variant="ghost" onClick={() => setPanel(null)}>Cancel</Button>
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                );
              })}
            </section>

            {/* Legend: shape + words, never colour alone */}
            <ul className="flex flex-wrap gap-3 text-xs text-muted-foreground" aria-label="Legend">
              <li className="flex items-center gap-1"><span className={cn('inline-block h-3 w-6 rounded-sm', BLOCK_STYLE.hands_on)} />Hands-on (measured, labor)</li>
              <li className="flex items-center gap-1"><span className={cn('inline-block h-3 w-6 rounded-sm', BLOCK_STYLE.unattended)} />Unattended machine (measured, no labor)</li>
              <li className="flex items-center gap-1"><span className={cn('inline-block h-3 w-6 rounded-sm', BLOCK_STYLE.paused)} />Switched away / paused</li>
              <li className="flex items-center gap-1"><span className={cn('inline-block h-3 w-6 rounded-sm', BLOCK_STYLE.planned)} />Planned (not measured)</li>
              <li className="flex items-center gap-1"><span className="inline-block h-3 w-0.5 bg-[hsl(var(--status-blocked))]" />Now</li>
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default TechnicianWorkspaceDialog;
